import React, { createContext, CSSProperties, ReactChild, ReactElement, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import cls from "classnames";
import { useLocation } from "react-router";
import FontAwesomeIcon from "../fa_icon";
import { Ribbon, RibbonProps } from "../ribbon";
import { ElementsInView, useElementsInView } from "../SectionLinkList/element_in_view_hook";
import styles from "./ViewSectionList.module.scss";
import { isEventFromCurrentTarget } from "../../../utils/dom";
import { TransitionedContainer, TransitionedContainerContext } from "../transitioned_container";

export type ViewSection = {
  /**
   * Optional view element key.
   */
  key?: string;

  /**
   * ID of the view element.
   */
  id: string;

  ribbon?: Omit<RibbonProps, 'isCompact'>;
  omitRibbon?: boolean;
  children: ReactChild;
  iconName?: string;

  /**
   * If non-null, then applies a transition when the value changes.
   */
  visible?: boolean;

  /**
   * Whether to have an appear animation.
   */
  appear?: boolean;

  /**
   * Whether to only render children with a `data-skeleton` attribute.
   * Elements with `data-skeleton` are rendered with a light background only.
   */
  skeleton?: boolean;

  /**
   * Allows for rendering a custom container, which is useful if
   * `skeleton` is true, such as a context provider override.
   */
  contain?: (children: ReactElement, key: string) => ReactElement;
}

type ViewSectionRibbonProps = Omit<RibbonProps, 'isCompact'>;

export type ViewSectionListProps = {
  ribbon: ViewSectionRibbonProps;
  sections: (ViewSection | null)[];
  style?: CSSProperties;
  compact?: boolean;
}

/**
 * Lists sections, with titles, subtitles, and more.
 */
export const ViewSectionList: React.FC<ViewSectionListProps> = ({ ribbon, sections, style, compact }) => {
  sections = sections.filter(section => section);

  sections.forEach((section, index) => {
    if (!section.ribbon && index > 0 && !section.omitRibbon) {
      throw new Error("Only the first section can omit its ribbon. Set `omitRibbon` to true to fix this error, and to confirm that this is intentional.");
    }
  });

  const location = useLocation();
  const [nonce, setNonce] = useState<any>(null);

  //  List of sections which have gone from visible: false to true to trigger a transition.
  //  Only relevant for sections with appear: true.
  const appearedSections = useRef<string[]>([]);

  const transitionContext = useContext(TransitionedContainerContext);

  const sectionIds = sections.map(section => section.id);

  appearedSections.current = appearedSections.current.filter(id =>
    sectionIds.includes(id));

  useEffect(() => {
    setTimeout(() => {
      sectionIds.forEach(id => {
        //  Add new sections (in this effect) to the list of appeared sections.
        if (!appearedSections.current.includes(id)) {
          appearedSections.current.push(id);
        }
      });

      setNonce(Date.now()); //  Trigger a transition.
    }, 50);

  }, [sectionIds.join()]);

  useLayoutEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) {
      return;
    }

    //  Replicate the behavior of location.hash auto-scroll. 
    const linkedSection = sections.find(section => section.id === hash);
    if (linkedSection) {
      document.getElementById(hash)?.scrollIntoView();
    }
  }, []);

  const elementsInView = useViewSectionsInView();

  return (
    <ViewSectionContext.Provider value={{ elementsInView }}>
      <div data-view-section-list="" style={style}>
        {
          ribbon ? (
            <Ribbon {...ribbon} />
          ) : null
        }
        <div className={styles.list}>
          {
            sections.map((section, index) => {
              const key = section.key || section.id;
              let element = (
                <SectionListItem
                  key={key}
                  section={section}
                  isFirst={index === 0}
                  compact={compact}
                />
              );

              let visible = section.visible;

              if (section.appear && !transitionContext?.isAnyTransition) {
                //  Prevent animation during transition.
                visible = (visible ?? true) && appearedSections.current.includes(section.id);
              }

              if (typeof visible === 'boolean') {
                element = (
                  <TransitionedContainer
                    key={key}
                    lock={visible}
                    overflowHiddenDuringTransition={false}
                    style={{ width: '100%' }}
                    transitionContainerStyle={{ width: '100%' }}>
                    {
                      visible ? element : (
                        <div></div>
                      )
                    }
                  </TransitionedContainer>
                );
              }

              if (section.contain) {
                element = section.contain(element, key);
              }

              return element;
            })
          }
        </div>
      </div>
    </ViewSectionContext.Provider>
  )
}

type SectionListItemProps = {
  section: ViewSection;
  isFirst?: boolean;
  compact?: boolean;
}

const SectionListItem: React.FC<SectionListItemProps> = ({ section, isFirst, compact }) => {
  return (
    <div className={cls([
      styles.itemContainer,
      compact ? styles.compact : null,
      isFirst ? styles.first : null
    ])}>
      <div className={cls([
        styles.item,
        section.skeleton ? styles.skeleton : null,
      ])}
        data-view-section={section.id}
        onAnimationEnd={event => {
          if (isEventFromCurrentTarget(event)) {
            //  Reset flash animation.
            event.currentTarget.removeAttribute('data-flash');
          }
        }}
        ref={rootElement => {
          if (!rootElement) {
            return;
          }

          //  Reset state.
          rootElement.querySelectorAll('[data-skeleton-container]').forEach(element =>
            element.removeAttribute('data-skeleton-container'));

          if (!section.skeleton) {
            return;
          }

          //  Set an attribute to keep containers of skeleton elements visible.
          const skeletonElements = rootElement.querySelectorAll('[data-skeleton]');
          for (const element of skeletonElements) {
            //  Add an offset to all skeleton elements.
            (element as HTMLElement).style.animationDelay = `${Math.round(element.getBoundingClientRect().top)}ms`;

            let parentElement = element.parentElement;

            while (!parentElement.isSameNode(rootElement)) {
              parentElement.setAttribute('data-skeleton-container', '');
              parentElement = parentElement.parentElement;
            }
          }
        }}>
        {/* First section should scroll all the way up. */}
        <div style={{ position: 'absolute', top: isFirst ? '-100vh' : '-25vh' }} id={section.id}></div>
        {
          section.ribbon ? (
            <div className={styles.header}>
              {
                false && section.iconName ? (
                  <div className={styles.iconContainer}>
                    <FontAwesomeIcon name={section.iconName} className={styles.icon} type="solid" />
                  </div>
                ) : null
              }
              <Ribbon
                {...section.ribbon}
                className={styles.ribbonTitle}
                isCompact
                titleStatic={false}
              />
            </div>
          ) : null
        }
        <div>
          {section.children}
        </div>
      </div>
    </div>
  )
}

/**
 * Context for view sections.
 * 
 * By using this, we only need 1 useElementsInView hook instead of several.
 */
export const ViewSectionContext = createContext<{
  elementsInView: ElementsInView;
}>(null);

/**
 * React hook which wraps `useElementsInView`
 * and works with view sections.
 */
export const useViewSectionsInView = () => {
  return useElementsInView('[data-view-section]',
    { getId: element => element.getAttribute('data-view-section'), midline: '30%' })
}

export function focusOnViewSection(id: string) {
  document.querySelector(`[data-view-section="${id}"]`)?.setAttribute('data-flash', 'true');
  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth"
  });
}